import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from '../lib/cn';

/**
 * Tabela do Trakd · glass com header sticky, divisórias sutis,
 * rows com hover. Use envolvido em `<Card padding="none">` se quiser
 * o glass-frame ao redor.
 *
 *   <Table>
 *     <TableHeader>
 *       <TableRow>
 *         <TableHead>Evento</TableHead>
 *         <TableHead className="text-right">Valor</TableHead>
 *       </TableRow>
 *     </TableHeader>
 *     <TableBody>
 *       <TableRow><TableCell>...</TableCell></TableRow>
 *     </TableBody>
 *   </Table>
 */

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(function Table(
  { className, ...props },
  ref,
) {
  return (
    <div className="scrollbar-soft w-full overflow-auto">
      <table
        ref={ref}
        className={cn('w-full caption-bottom border-collapse text-sm', className)}
        {...props}
      />
    </div>
  );
});

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableHeader({ className, ...props }, ref) {
  return (
    <thead
      ref={ref}
      className={cn(
        'bg-surface-elevated/40 backdrop-blur-md',
        '[&_tr]:border-border-subtle [&_tr]:border-b',
        className,
      )}
      {...props}
    />
  );
});

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, ...props }, ref) {
  return (
    <tbody
      ref={ref}
      className={cn(
        '[&_tr:last-child]:border-0',
        '[&_tr]:border-border-subtle/60 [&_tr]:border-b',
        className,
      )}
      {...props}
    />
  );
});

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableFooter({ className, ...props }, ref) {
  return (
    <tfoot
      ref={ref}
      className={cn(
        'bg-surface-elevated/30 border-border-subtle border-t font-medium [&_tr]:border-0',
        className,
      )}
      {...props}
    />
  );
});

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  function TableRow({ className, ...props }, ref) {
    return (
      <tr
        ref={ref}
        className={cn(
          'hover:bg-surface-elevated/40 transition-colors',
          'data-[state=selected]:bg-surface-elevated/60',
          className,
        )}
        {...props}
      />
    );
  },
);

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  function TableHead({ className, ...props }, ref) {
    return (
      <th
        ref={ref}
        className={cn(
          'h-11 px-5 text-left align-middle',
          'text-ink-subtle font-mono text-[10px] font-semibold tracking-[0.14em] uppercase',
          'whitespace-nowrap',
          className,
        )}
        {...props}
      />
    );
  },
);

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  function TableCell({ className, ...props }, ref) {
    return (
      <td ref={ref} className={cn('text-ink px-5 py-3.5 align-middle', className)} {...props} />
    );
  },
);

export const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(function TableCaption({ className, ...props }, ref) {
  return <caption ref={ref} className={cn('text-ink-muted mt-4 text-sm', className)} {...props} />;
});
